import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import CommonPagination from "../../components/Table/CommonPagination";
import { getUserPagentHistory } from "../../http/userModification";

const PAGE_SIZE = 20;

const MATCH_TYPE_LABELS = {
  PROMPT_POST: "Prompt",
  LIVE: "Live",
  CONTEST_POST: "Contest",
  CATEGORY_POST: "Category",
  KARAOKE_POST: "Karaoke",
  DANCE_POST: "Dance",
  ART_POST: "Art",
  CHESS: "Chess",
  DEBATE: "Debate",
  GROUP_DUET_POST: "Group duet",
};

const RESULT_CONFIG = {
  WIN: { label: "Win", bg: "#e8f5e9", color: "#2e7d32" },
  LOSS: { label: "Loss", bg: "#ffebee", color: "#c62828" },
  TIE: { label: "Tie", bg: "#f5f5f5", color: "#616161" },
  IN_PROGRESS: { label: "In progress", bg: "#e3f2fd", color: "#1565c0" },
  CANCELLED: { label: "Cancelled", bg: "#f5f5f5", color: "#757575" },
  UNKNOWN: { label: "Unknown", bg: "#f5f5f5", color: "#757575" },
};

const headerCellStyle = {
  fontSize: "12px",
  color: "#000",
  fontWeight: 600,
  backgroundColor: "#e0e0e0",
  whiteSpace: "nowrap",
};

const bodyCellStyle = {
  fontSize: "12px",
  color: "#333",
  verticalAlign: "top",
};

function resultConfig(result) {
  return RESULT_CONFIG[result] || RESULT_CONFIG.UNKNOWN;
}

function formatWhen(match) {
  const stamp = match.matchEndTime || match.matchFoundTime;
  return stamp ? dayjs(stamp).format("MM/DD/YY HH:mm") : "—";
}

function VoteScore({ match }) {
  if (!match.hasVoteScores) {
    return (
      <Typography sx={{ fontSize: "12px", color: "#888" }}>—</Typography>
    );
  }

  const userVotes = match.userVotes ?? 0;
  const opponentVotes = match.opponentVotes ?? 0;
  const userAhead = userVotes > opponentVotes;
  const opponentAhead = opponentVotes > userVotes;

  return (
    <Typography sx={{ fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap" }}>
      <span style={{ color: userAhead ? "#2e7d32" : opponentAhead ? "#c62828" : "#333" }}>
        {userVotes}
      </span>
      <span style={{ color: "#999", fontWeight: 500 }}> – </span>
      <span style={{ color: opponentAhead ? "#2e7d32" : userAhead ? "#c62828" : "#333" }}>
        {opponentVotes}
      </span>
    </Typography>
  );
}

const UserPagentHistoryTab = ({ username }) => {
  const [matches, setMatches] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchHistory = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    setError("");
    try {
      const response = await getUserPagentHistory({
        username,
        page,
        limit: PAGE_SIZE,
      });
      const payload = response?.data || response;
      setMatches(payload?.matches || []);
      setTotalCount(payload?.totalCount || 0);
    } catch (err) {
      setMatches([]);
      setTotalCount(0);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.data?.message ||
          err?.message ||
          "Failed to load pagent history",
      );
    } finally {
      setLoading(false);
    }
  }, [username, page]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography sx={{ fontSize: "13px", color: "#666" }}>
          {totalCount} pagent{totalCount === 1 ? "" : "s"}. Vote scores are
          you–opponent (weighted totals). Chess has no vote score. Hidden rows
          were removed from the public feed, usually after a ban.
        </Typography>
        <Button
          size="small"
          sx={{ textTransform: "none" }}
          onClick={fetchHistory}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {error && (
        <Typography color="error" sx={{ fontSize: "13px", mb: 1 }}>
          {error}
        </Typography>
      )}

      <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellStyle}>When</TableCell>
              <TableCell sx={headerCellStyle}>Type</TableCell>
              <TableCell sx={headerCellStyle}>Opponent</TableCell>
              <TableCell sx={headerCellStyle}>Result</TableCell>
              <TableCell sx={headerCellStyle}>Votes (you – opp)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {matches.map((match) => {
              const result = resultConfig(match.result);
              return (
                <TableRow key={match._id}>
                  <TableCell sx={{ ...bodyCellStyle, whiteSpace: "nowrap" }}>
                    <Tooltip title={match._id} arrow>
                      <span>{formatWhen(match)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      <Chip
                        size="small"
                        label={MATCH_TYPE_LABELS[match.matchType] || match.matchType || "Match"}
                        sx={{ fontSize: "11px" }}
                      />
                      {match.isHidden && (
                        <Chip
                          size="small"
                          label="Hidden"
                          sx={{ fontSize: "11px", bgcolor: "#eeeeee" }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    {match.opponentLabel || "—"}
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    <Chip
                      size="small"
                      label={result.label}
                      sx={{
                        fontSize: "11px",
                        fontWeight: 700,
                        bgcolor: result.bg,
                        color: result.color,
                        mb: 0.5,
                      }}
                    />
                    <Typography sx={{ fontSize: "12px", color: "#333" }}>
                      {match.statement || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    <VoteScore match={match} />
                  </TableCell>
                </TableRow>
              );
            })}
            {!matches.length && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#666" }}>
                  {error ? "Could not load pagent history" : "No pagent history yet"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalCount > PAGE_SIZE && (
        <Box sx={{ mt: 1.5 }}>
          <CommonPagination
            rowsPerPage={PAGE_SIZE}
            count={totalCount}
            page={page - 1}
            onPageChange={(_, newPage) => setPage(newPage + 1)}
          />
        </Box>
      )}
    </Box>
  );
};

export default UserPagentHistoryTab;
